import { Box, CardContent } from "@mui/material";

import CommonDashboardCard from "../commonDashboardCard";

import { Area, AreaChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import ChartTooltip from "../chartTooltip";
import { useIntl } from 'react-intl';

const getNiceStep = value => {
  if (value <= 1) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalizedValue = value / magnitude;
  const normalizedStep = [1, 2, 2.5, 5, 10]
    .find(step => normalizedValue <= step) || 10;

  return Math.max(1, Math.ceil(normalizedStep * magnitude));
};

const getYAxisConfig = data => {
  const maximumValue = data.reduce((maximum, item) => {
    const value = Number(item?.total);
    return Number.isFinite(value) ? Math.max(maximum, value) : maximum;
  }, 0);
  const step = getNiceStep(maximumValue / 5);
  const roundedMaximum = Math.ceil(maximumValue / step) * step;
  const maximum = Math.max(
    step,
    roundedMaximum > maximumValue ? roundedMaximum : roundedMaximum + step
  );
  const ticks = Array.from(
    { length: Math.round(maximum / step) + 1 },
    (_, index) => index * step
  );

  return { maximum, ticks };
};

export default function TicketsByHourCard({ data = [] }) {
  const intl = useIntl();
  const yAxis = getYAxisConfig(data);

  return (
    <CommonDashboardCard
      cardHeaderProps={{
        title: intl.formatMessage({
          id: 'dashboard.new.operation.openingHours.title',
        })
      }}
      info={intl.formatMessage({
        id: 'dashboard.new.operation.openingHours.info',
      })}
    >
      <CardContent
        sx={{
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Box sx={{ width: '100%', height: 285 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{
                top: 30,
                right: 20,
                bottom: 5,
                left: -20,
              }}
            >
              <defs>
                <linearGradient
                  id="ticketsGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor="#3478f6"
                    stopOpacity={0.25}
                  />

                  <stop
                    offset="70%"
                    stopColor="#3478f6"
                    stopOpacity={0.08}
                  />

                  <stop
                    offset="100%"
                    stopColor="#3478f6"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                vertical={false}
                stroke="#e7ebf1"
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="hour"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={{
                  fontSize: 10,
                  fill: '#6b7280',
                }}
              />

              <YAxis
                domain={[0, yAxis.maximum]}
                ticks={yAxis.ticks}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 10,
                  fill: '#6b7280',
                }}
              />

              <Tooltip content={<ChartTooltip />} />

              <Area
                type="linear"
                dataKey="total"
                stroke="#3478f6"
                strokeWidth={2}
                fill="url(#ticketsGradient)"
                fillOpacity={1}
                isAnimationActive
                dot={{
                  r: 3,
                  fill: '#3478f6',
                  stroke: '#ffffff',
                  strokeWidth: 1,
                }}
                activeDot={{
                  r: 5,
                  fill: '#3478f6',
                  stroke: '#ffffff',
                  strokeWidth: 2,
                }}
              >
                <LabelList
                  dataKey="total"
                  position="top"
                  offset={9}
                  style={{
                    fontSize: 10,
                    fill: '#374151',
                    fontWeight: 600,
                  }}
                />
              </Area>
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </CommonDashboardCard>
  );
}
